import useOfferList from 'hooks/Offers/useOfferList'
import { usePlaceByName } from 'hooks/usePlace'
import { take } from 'lib/array/arrayUtils'
import { useContext, useMemo } from 'react'
import { useEffect } from 'react'
import { fetchMaxSavings } from 'actions/HomepageActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { MAX_CAROUSEL_ITEM_COUNT } from 'home/components/OfferCarousels/common/constants'
import { ANYWHERE_PLACE_ID } from 'constants/search'
import { DEFAULT_OFFER_LIST, HOME_PAGE_OFFER_TYPES } from 'home/constants'
import GeoContext from 'contexts/geoContext'
import { getHomeOfferListParams } from 'home/helpers'
import { EmptyObject } from 'lib/object/objectUtils'

export function useExcludedOfferIds(destination: string): string[] {
  const destinationId = usePlaceByName(destination)?.id

  const params = destinationId ? { ...DEFAULT_OFFER_LIST, destinationId, frontPageVisibleOnly: false } : DEFAULT_OFFER_LIST
  const [offerList] = useOfferList(params)

  // exclude the first 2 offers from hero carousel from other carousels on the page
  const excludedIds = useMemo(() => take(offerList.offerIds, 2), [offerList.offerIds])
  return excludedIds
}

export function useDestinationMaxSavings(
  region: string,
  destinationIds: string[],
  selectedFilter: App.DestinationSelectedFilters,
): App.DestinationMaxSavings {
  const dispatch = useAppDispatch()
  const maxSavings = useAppSelector(state => state.destination.maxSavings)

  useEffect(() => {
    if (maxSavings && Object.keys(maxSavings).length &&
      maxSavings[selectedFilter] && Object.keys(maxSavings[selectedFilter]).length) return
    dispatch(fetchMaxSavings(region, destinationIds, selectedFilter))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter])

  return maxSavings ?? EmptyObject
}

export function useHeroOfferListIds(destination?: string, sortOption?: App.OfferListSortByOptions) {
  const { currentRegionCode, geoStateName } = useContext(GeoContext)
  const destinationId = usePlaceByName(destination)?.id ?? ANYWHERE_PLACE_ID

  const params = useMemo(() => {
    if (destination) {
      return {
        offerTypes: HOME_PAGE_OFFER_TYPES,
        limit: MAX_CAROUSEL_ITEM_COUNT,
        placeIds: [destinationId],
      }
    } else {
      return getHomeOfferListParams({
        region: currentRegionCode,
        subregion: geoStateName,
        sortOption,
      })
    }
  }, [currentRegionCode, destination, destinationId, geoStateName, sortOption])

  const [offerList] = useOfferList(params)
  return offerList.offerIds
}
